import { computed, inject, Injectable, signal } from "@angular/core";
import { OAuthErrorEvent, OAuthInfoEvent, OAuthService } from "angular-oauth2-oidc";
import { BehaviorSubject, tap } from "rxjs";
import { AuthorizationService } from "./authorization.service";
import { environment } from '../environments/environment';

@Injectable({providedIn: "root"})
export class AuthenticationService
{
  oauthService = inject(OAuthService);
  authorizationService = inject(AuthorizationService);

  private _isAuthenticated$ = new BehaviorSubject<boolean>(false);
  private _isLoaded$ = new BehaviorSubject<boolean>(false);
  isAuthenticated$ = this._isAuthenticated$.asObservable();
  isLoaded$ = this._isLoaded$.asObservable();

  isLoggedIn = signal<boolean>(false);
  userName = computed(() => this.claims()['preferred_username'] ?? this.claims()['username'] ?? '');
  groups = computed(() => this.claims()['cognito:groups'] ?? []);
  claims = signal<any>(null);

  constructor() {
    this.oauthService.events.pipe(
      tap(event => {
        console.log("AuthenticationService event", event);
        switch(event.type) {
          case "token_received":
            this.setAuthenticationInformation();
            break;
  
          case "token_expires":
            if ((<OAuthInfoEvent>event).info === 'access_token') {
              this.oauthService.refreshToken();
            }
            break;
  
          case "discovery_document_loaded":
            console.log("✅ Discovery document wurde geladen.");
            break;
  
          case "discovery_document_load_error":
            console.error("❌ Fehler beim Laden des Discovery Documents.");
            break;
        }
      })
    ).subscribe();

    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      console.log("✅ discovery_document_loaded complete");
  
      if (!this.oauthService.hasValidAccessToken()) {
        console.log("❌ Kein gültiger Token – starte Login");
        this.login();
      } else {
        console.log("✅ Gültiger Token – Benutzer einloggen");
        this.setAuthenticationInformation();
      }
    }).catch(err => {
      console.error("❌ Fehler bei loadDiscoveryDocumentAndTryLogin:", err);
    });
  }

  private b64DecodeUnicode(str: string): string {
    const base64 = str.replace(/-/g, '+').replace(/_/g, '/');

    return decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
  }

  private padBase64(base64data: string): string {
    while (base64data.length % 4 !== 0) {
      base64data += '=';
    }
    return base64data;
  }

  private resetAuthenticationInformation() {
    this.isLoggedIn.set(false);
    this.claims.set(null);
  }

  private setAuthenticationInformation() {
    if (this.oauthService.hasValidAccessToken()) {
      this.setClaimsAndUsername();
      this.authorizationService.setRoles(this.claims()['cognito:groups'] ?? []);
      this.isLoggedIn.set(true);
      this._isAuthenticated$.next(true);
      this._isLoaded$.next(true);
    }
  }

  private setClaimsAndUsername(): void {
    const accessToken = this.oauthService.getAccessToken();
    const accessClaims = JSON.parse(this.b64DecodeUnicode(this.padBase64(accessToken.split(".")[1])));

    const idToken = this.oauthService.getIdToken();
    const idClaims = idToken ? JSON.parse(this.b64DecodeUnicode(this.padBase64(idToken.split(".")[1]))) : {};

    // Merge: access token claims take priority, but ID token provides preferred_username
    const claims = { ...idClaims, ...accessClaims, preferred_username: idClaims['preferred_username'] ?? accessClaims['preferred_username'] };
    this.claims.set(claims);
  }

  public login() {
    this.oauthService.initCodeFlow();
  }

  logout(): void {
    this.oauthService.logOut(); // optional: löscht Tokens
  
    // alternativ oder zusätzlich
    this.oauthService.logOut(true); // probiert Full Logout
    this.oauthService.revokeTokenAndLogout(); // für Sicherheit
  
    this.oauthService.logOut();            // Optional
  
    sessionStorage.clear();
    localStorage.clear(); // nur wenn du keine anderen Daten speicherst
  
    // Dann redirect zur Cognito-Logout-URL
    const idToken = this.oauthService.getIdToken();
    const clientId = environment.cognitoClientId;
    const redirectUri = window.location.origin;
  
    const logoutUrl =
      environment.cognitoLogoutUrl +
      `?client_id=${clientId}` +
      `&logout_uri=${encodeURIComponent(redirectUri)}` +
      `&id_token_hint=${idToken}`;

    this._isAuthenticated$.next(false);

    window.location.href = logoutUrl;
  }
}
